import React, { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Cone, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { GsUserMenu } from './GsUserMenu';

export interface SidebarItem {
  key: string;
  label: string;
  icon: LucideIcon;
  path: string;
  adminOnly?: boolean;
  badge?: string | number;
  externalUrl?: boolean;
}

export interface SidebarBottomUser {
  name: string;
  email?: string;
  role: string;
  initials: string;
}

/** data-tour pour tours guidés (ex. driver.js) — clés par chemin d’app */
const SIDEBAR_TOUR_ATTR: Record<string, string> = {
  '/': 'nav-dashboard',
  '/rapports': 'nav-rapports',
  '/vue-globale': 'nav-overview',
  '/apercu': 'nav-overview',
  '/recherche': 'nav-recherche',
  '/rappels': 'nav-rappels',
  '/finances': 'nav-finances',
  '/financier': 'nav-finances',
  '/materiel': 'nav-materiel',
  '/articles': 'nav-materiel',
  '/utilisateurs': 'nav-utilisateurs',
  '/parametres': 'nav-parametres',
};

export interface GsSidebarProps {
  appName: string;
  items: SidebarItem[];
  adminItems?: SidebarItem[];
  isAdmin?: boolean;
  activePath?: string;
  onNavigate?: (path: string) => void;
  /** Relancer un tour guidé (ex. depuis le menu utilisateur) */
  onStartTour?: () => void;
  LinkComponent?: React.ComponentType<{ to: string; className?: string; children: React.ReactNode }>;
  bottomUser?: SidebarBottomUser;
  variant?: 'navy' | 'light';
  headerLogoSrc?: string;
  /** Logo alternatif en mode sombre (ex. symbole blanc). Si absent : `headerLogoSrc`. */
  headerLogoSrcDark?: string;
  mainSectionLabel?: string;
  adminSectionLabel?: string;
  isDark?: boolean;
  onToggleDark?: () => void;
  onLogout?: () => void;
  appVersion?: string;
  /** Clé localStorage pour persister l'état collapsed (défaut: 'gs_sidebar_collapsed') */
  storageKey?: string;
}

/** Tooltip minimal affiché à droite d'un item quand la sidebar est rétractée */
function ItemTooltip({ label }: { label: string }) {
  return (
    <div
      className={[
        'pointer-events-none absolute left-full ml-2 z-50',
        'whitespace-nowrap rounded-md bg-slate-800 px-2.5 py-1.5',
        'text-[11px] font-medium text-white shadow-lg',
        'opacity-0 group-hover:opacity-100 transition-opacity duration-150',
      ].join(' ')}
    >
      {label}
      {/* Flèche gauche */}
      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
    </div>
  );
}

function useSidebarCollapsed(storageKey: string) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, String(next)); } catch { /* noop */ }
      return next;
    });
  };

  return { collapsed, toggle };
}

export function GsSidebar({
  appName,
  items,
  adminItems,
  isAdmin,
  activePath = '/',
  onNavigate,
  onStartTour,
  LinkComponent,
  bottomUser,
  variant = 'navy',
  headerLogoSrc,
  headerLogoSrcDark,
  mainSectionLabel,
  adminSectionLabel = 'Administration',
  isDark = false,
  onToggleDark,
  onLogout,
  appVersion,
  storageKey = 'gs_sidebar_collapsed',
}: GsSidebarProps) {
  const isLight = variant === 'light';
  const { collapsed, toggle } = useSidebarCollapsed(storageKey);

  const logoSrc =
    isDark && headerLogoSrcDark ? headerLogoSrcDark : headerLogoSrc;

  // Largeur : 192px déployée, 52px rétractée
  const width = collapsed ? 'w-[52px]' : 'w-[192px]';

  const shell = isLight
    ? 'bg-white dark:bg-slate-900 border-r border-[#ececec] dark:border-slate-800'
    : 'bg-[#0f172a]';

  const titleClass    = isLight ? 'text-slate-800 dark:text-slate-100' : 'text-white';
  const subtitleClass = isLight ? 'text-slate-400 dark:text-slate-500' : 'text-[#334155]';

  const sectionLabelClass = `px-2 mb-1 mt-3 text-[9px] font-semibold uppercase tracking-[0.12em] ${
    isLight ? 'text-slate-400 dark:text-slate-500' : 'text-[#334155]'
  }`;

  const footBorder = isLight
    ? 'border-t border-[#ececec] dark:border-slate-800'
    : 'border-t-[0.5px] border-t-[rgba(255,255,255,0.06)]';

  const badgeClass = isLight
    ? 'rounded-full bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:text-slate-200 tabular-nums'
    : 'rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-white/90 tabular-nums';

  const renderItem = (item: SidebarItem) => {
    const tourKey = SIDEBAR_TOUR_ATTR[item.path];
    const tourProps = tourKey ? ({ 'data-tour': tourKey } as const) : {};

    const Icon = item.icon;
    const isExternal = item.externalUrl === true;
    const isActive =
      !isExternal &&
      (activePath === item.path || (item.path !== '/' && activePath.startsWith(item.path)));

    const base = collapsed
      ? 'relative group w-full flex items-center justify-center rounded-md py-2 transition-colors border-l-[2px]'
      : 'w-full flex items-center gap-2 rounded-md py-1.5 pl-2 pr-2 text-[12px] transition-colors border-l-[2px]';

    const activeCls = isActive
      ? 'bg-brand-500/8 text-brand-500 font-medium border-brand-500'
      : isLight
        ? 'text-slate-500 border-transparent hover:bg-[#f5f5f5] hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800'
        : 'text-[#64748b] border-transparent hover:bg-[rgba(255,255,255,0.04)]';

    const className = `${base} ${activeCls}`;

    const iconEl = (
      <>
        <Icon size={15} className="shrink-0 opacity-80" />
        {collapsed && <ItemTooltip label={item.label} />}
      </>
    );

    const row = collapsed ? iconEl : (
      <span className="flex w-full min-w-0 items-center justify-between gap-2">
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <Icon size={15} className="shrink-0 opacity-80" />
          <span className="truncate">{item.label}</span>
          {isExternal && <ExternalLink size={11} className="shrink-0 opacity-60" aria-hidden />}
        </span>
        {item.badge != null && item.badge !== '' && (
          <span className={badgeClass}>{item.badge}</span>
        )}
      </span>
    );

    if (isExternal) {
      return (
        <a
          key={item.key}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={`${className} no-underline`}
          title={collapsed ? item.label : undefined}
          {...tourProps}
        >
          {row}
        </a>
      );
    }
    if (LinkComponent) {
      return (
        <LinkComponent key={item.key} to={item.path} className={className} {...tourProps}>
          {row}
        </LinkComponent>
      );
    }
    return (
      <button
        key={item.key}
        type="button"
        onClick={() => onNavigate?.(item.path)}
        className={className}
        title={collapsed ? item.label : undefined}
        {...tourProps}
      >
        {row}
      </button>
    );
  };

  return (
    <aside
      data-tour="sidebar"
      className={[
        'relative hidden h-full shrink-0 flex-col text-left md:flex',
        'transition-[width] duration-200 ease-in-out',
        width,
        shell,
      ].join(' ')}
    >
      {/* Bouton collapse — flottant sur le bord droit, au niveau du logo */}
      <button
        type="button"
        onClick={toggle}
        title={collapsed ? 'Déployer la sidebar' : 'Réduire la sidebar'}
        aria-label={collapsed ? 'Déployer la sidebar' : 'Réduire la sidebar'}
        className={[
          'absolute top-[18px] -right-[12px] z-50',
          'flex h-6 w-6 items-center justify-center rounded-full',
          'border shadow-md transition-colors',
          isLight
            ? 'bg-white border-[#ececec] text-slate-400 hover:text-slate-700 hover:border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-500 dark:hover:text-slate-300'
            : 'bg-[#1e293b] border-white/10 text-slate-400 hover:text-white',
        ].join(' ')}
      >
        {collapsed
          ? <ChevronRight size={12} strokeWidth={2.5} />
          : <ChevronLeft size={12} strokeWidth={2.5} />}
      </button>

      {/* Logo + nom app */}
      <div className="shrink-0 px-3 py-3 border-b border-[#ececec] dark:border-slate-800">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2.5'}`}>
          {logoSrc ? (
            <img
              src={logoSrc}
              alt=""
              width={32}
              height={32}
              className="h-[32px] w-[32px] shrink-0 object-contain"
            />
          ) : (
            <div className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-md bg-brand-500" aria-hidden>
              <Cone className="h-4 w-4 text-white" strokeWidth={2.25} />
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className={`truncate text-[13px] font-semibold ${titleClass}`}>{appName}</div>
              <div className={`truncate text-[10px] ${subtitleClass}`}>Groupe Signalisation</div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="min-h-0 flex-1 overflow-y-auto px-1.5 py-2 space-y-0.5">
        {!collapsed && mainSectionLabel && (
          <div className={sectionLabelClass}>{mainSectionLabel}</div>
        )}
        <div className="space-y-0.5">{items.filter((i) => !i.adminOnly).map(renderItem)}</div>
        {isAdmin && adminItems && adminItems.length > 0 && (
          <>
            {!collapsed && <div className={sectionLabelClass}>{adminSectionLabel}</div>}
            <div className="space-y-0.5">{adminItems.map(renderItem)}</div>
          </>
        )}
      </nav>

      {/* User menu */}
      {bottomUser && (
        <div className={`shrink-0 ${footBorder}`}>
          <GsUserMenu
            user={{
              name: bottomUser.name,
              email: bottomUser.email,
              role: bottomUser.role,
              initials: bottomUser.initials,
            }}
            isDark={isDark}
            onToggleDark={onToggleDark}
            onLogout={onLogout}
            onStartTour={onStartTour}
            variant={variant}
            appName={appName}
            version={appVersion}
            collapsed={collapsed}
          />
        </div>
      )}
    </aside>
  );
}
